import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { User, Kid } from '../types';
import * as api from '../api/client';

interface AuthState {
  user: User | null;
  kids: Kid[];
  loading: boolean;
  error: string | null;
  login: (phoneNumber: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [kids, setKids] = useState<Kid[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const data = await api.getProfile();
      setUser(data.user);
      setKids(data.kids);
      setError(null);
    } catch {
      setUser(null);
      setKids([]);
    }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const login = async (phoneNumber: string) => {
    setError(null);
    setLoading(true);
    try {
      const data = await api.login(phoneNumber);
      setUser(data.user);
      await refresh();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    setKids([]);
  };

  return (
    <AuthContext.Provider value={{ user, kids, loading, error, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
